/**
 * K-12 districts domain — React Query hooks.
 *
 * Post-FIR-6997 the K-12 table reads/mutates through the unified
 * opportunities engine (`services/opportunities`, `common_table_actions`),
 * so this domain's list/summary hooks and wrapped bulk mutations were
 * removed as dead code. What remains: the detail hook the district detail
 * pages consume, and the counties lookup the K12 filterset consumes.
 */

import { useQuery, type UseQueryOptions } from '@tanstack/react-query'
import axios, { AxiosError } from 'axios'

import {
  getK12DistrictQueryKey,
  useListK12DistrictsCounties,
} from '../generated/hooks/k12Controller'
import client from '../apiClient'

import type {
  GetK12DistrictQueryResponse,
  GetK12District404,
} from '../generated/types/GetK12District'

export { useListK12DistrictsCounties }

export type {
  GetK12DistrictQueryResponse,
  GetK12DistrictPathParams,
} from '../generated/types/GetK12District'

/**
 * Get K-12 district detail.
 *
 * Hand-written wrapper around the generated `getK12District` client
 * because the legacy hook (`k12api.useGetK12District`) skips retries on
 * 404 — without that predicate, React Query retries 3× before
 * surrendering, which delays "not found" feedback in the UI. The
 * wrapper accepts an optional id (so callers can pass `undefined`
 * before route resolution) and disables the query until the id arrives.
 */
export function useGetK12District(
  id?: number,
  options: {
    query?: Omit<
      UseQueryOptions<
        GetK12DistrictQueryResponse,
        AxiosError<GetK12District404>
      >,
      'queryKey' | 'queryFn' | 'enabled' | 'retry'
    >
  } = {}
) {
  const { query = {} } = options
  return useQuery<GetK12DistrictQueryResponse, AxiosError<GetK12District404>>({
    queryKey:
      id !== undefined ? getK12DistrictQueryKey({ id }) : ['k12district', id],
    // FIR-7878: unified chain detail endpoint (byte-identical response).
    queryFn: async ({ signal }) =>
      (
        await client<GetK12DistrictQueryResponse>({
          method: 'GET',
          url: `/api/v1/opportunities/detail/${id}/`,
          params: { target_type: 'k12' },
          signal,
        })
      ).data,
    enabled: id !== undefined,
    retry: (failureCount, error) =>
      axios.isAxiosError(error) && error?.response?.status === 404
        ? false
        : failureCount < 3,
    ...query,
  })
}
